import { getCalendarRates } from "apis/calendarRates";
import ConditionalLink from "components/ConditionalLink";
import { InputDateRangeDrawer } from "components/DateRangeInput/InputDateRangeDrawer";
import { InputDateRangeDropdown } from "components/DateRangeInput/InputDateRangeDropdown";
import AddonButton from "components/Design/Button/AddonButton";
import PrimaryButtonFullWidth from "components/Design/Button/PrimaryButtonFullWidth";
import InputFloatLabel from "components/Design/Input/InputFloatLabel";
import RoomGuestSelect from "components/RoomGuestSelect/RoomGuestSelect";
import RoomGuestSelectDrawer from "components/RoomGuestSelect/RoomGuestSelectDrawer";
import PageContext from "contexts/PageContext";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { useTranslation } from "gatsby-plugin-react-i18next";
import useBookingCode from "hooks/useBookingCode";
import useBookingFormLink from "hooks/useBookingFormLink";
import React, { useContext, useEffect, useState } from "react";
import { ISO_FORMAT } from "store/constants/date";
import { PACKAGE } from "store/constants/group";
import { JAKARTA, JAKARTA_LOCATION, KOMODO } from "store/constants/hotel";
import { QUARANTINE } from "store/constants/tag";
import { checkBookingCodeRule } from "utils/bookingCodeValidation";
import { getDiffNights } from "utils/dates";
import dayjs from "utils/dayjs";
import { renderForMobile } from "utils/renderForMobile";
import ClearTextInput from "./Icons/ClearTextIcon";
import Cookies from "js-cookie";
const BookingFormHorizontal = ({
  forceMobile,
  location,
  hotelCode,
  roomCode,
  minimumStay,
  bookingCode: bookingCodeFromProps,
  stayPeriod,
  isPackage,
  showBackground,
  packageCode,
  hotelId,
  roomId,
  minLeadDay,
}) => {
  const { t } = useTranslation(["homepage", "bookingForm"]);
  const { isQuarantinePackage } = useContext(PageContext);
  const [dateRate, setDateRate] = useState(null);

  const isGeolocationSG = Cookies.get("AYANA_COUNTRY_CODE") === "SG";
  const isGeolocationUS = Cookies.get("AYANA_COUNTRY_CODE") === "US";

  useEffect(() => {
    const getDateRatesLocation = async () => {
      const rates = await getCalendarRates({
        location_code: location,
      });

      setDateRate(rates);
    };

    const getDateRatesHotel = async () => {
      const rates = await getCalendarRates({
        hotel_code_id: hotelId,
        location_code: location,
      });

      setDateRate(rates);
    };

    const getDateRatesRoom = async () => {
      const rates = await getCalendarRates({
        room_type_id: roomId,
        location_code: location,
      });

      setDateRate(rates);
    };

    if (location === JAKARTA || location === JAKARTA_LOCATION) return;
    if (hotelId && !roomId) {
      getDateRatesHotel();
    } else if (roomId) {
      getDateRatesRoom();
    } else {
      getDateRatesLocation();
    }
  }, [location, hotelId, roomId]);

  const breakpoints = useBreakpoint();
  const [guest, setGuest] = useState({
    rooms: 1,
    adults: 2,
    children: 0,
    infants: 0,
  });
  const [bookingCode, setBookingCode] = useBookingCode(bookingCodeFromProps);
  const [startDate, setStartDate] = useState(
    stayPeriod &&
      dayjs(stayPeriod.start_date).isAfter(dayjs().format(ISO_FORMAT))
      ? dayjs(stayPeriod.start_date).format(ISO_FORMAT)
      : minLeadDay
      ? dayjs().add(minLeadDay, "day").format(ISO_FORMAT)
      : dayjs().format(ISO_FORMAT)
  );
  const [endDate, setEndDate] = useState(
    typeof minimumStay === "number"
      ? dayjs(startDate).add(minimumStay, "day").format(ISO_FORMAT)
      : dayjs(startDate).add(1, "day").format(ISO_FORMAT)
  );
  const [saveLabel, setSaveLabel] = useState(
    t("bookingForm:save_night", {
      count:
        typeof minimumStay === "number"
          ? minimumStay
          : getDiffNights(startDate, endDate),
    })
  );

  const setDateValues = (dates) => {
    if (dates.startDate && dates.endDate) {
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
    }
  };

  const setGuestCount = (updatedGuest) => {
    setGuest((guest) => ({
      ...guest,
      ...updatedGuest,
    }));
  };

  const isJakartaLocation =
    location === JAKARTA || location === JAKARTA_LOCATION;

  const { link, handleInvalidSearch } = useBookingFormLink({
    startDate,
    endDate,
    rooms: guest.rooms,
    adults: guest.adults,
    children: guest.children,
    infants: guest.infants,
    hotelCode,
    roomCode,
    location,
    bookingCode,
    tags: isQuarantinePackage ? [QUARANTINE] : [],
    group: isPackage && PACKAGE,
    packageCode: packageCode || undefined,
  });

  // when only startdate is selcted and endDate is not
  // set the no of nights to 1
  useEffect(() => {
    if (!dayjs(endDate).isValid()) {
      setSaveLabel(
        t("bookingForm:save_night", {
          count: 1,
        })
      );
    }
  }, [endDate]);

  const onChange = (dates) => {
    if (dates.startDate && dates.endDate) {
      const diffNights = dates.endDate?.diff(dates.startDate, "days");
      setSaveLabel(t("bookingForm:save_night", { count: diffNights }));
    }
  };

  const validateBookingCodeOnKeyPress = (e) => {
    if (checkBookingCodeRule(e)) {
      e.preventDefault();
      return false;
    }
  };

  const viewRatesButton = (
    <AddonButton
      label={
        location?.toUpperCase() === KOMODO.toUpperCase() ||
        (location?.toUpperCase() === JAKARTA.toUpperCase() &&
          !isGeolocationSG &&
          !isGeolocationUS)
          ? t("View Rates")
          : t("bookingForm:jakarta_signup")
      }
      rightRounded
      onClick={handleInvalidSearch}
    />
  );

  const jakartaLink = `https://be.synxis.com/?adult=${
    guest.adults
  }&arrive=${startDate}&chain=10237&child=${
    guest.children
  }&currency=IDR&current_lang=id&current_location=jakarta&depart=${endDate}&hotel=10526&language=en-US&level=hotel&locale=en-US&rooms=${
    guest.rooms
  }&type=room-reservations&promo=${
    bookingCode ? bookingCode?.toUpperCase() : ""
  }`;

  return (
    <div
      className={`flex lg:items-center lg:justify-center flex-col lg:flex-row gap-16 ${
        showBackground && "bg-solid-white p-20"
      }`}>
      <div>
        <div className="container max-w-screen-xl m-auto">
          <div className="z-10 grid grid-cols-2 lg:grid-cols-5 lg:shadow-input-booking">
            <div className="w-full grid-cols-2 col-span-2 lg:col-span-2 shadow-input-booking lg:shadow-none">
              {renderForMobile(breakpoints) || forceMobile ? (
                <InputDateRangeDrawer
                  note={isJakartaLocation ? undefined : t("price_note")}
                  title={t("stayingDate")}
                  drawerProps={{
                    placement: "bottom",
                    height: "100%",
                  }}
                  dateRate={dateRate}
                  onSave={setDateValues}
                  onChange={onChange}
                  endDate={endDate}
                  startDate={startDate}
                  saveCta={(onClick) => (
                    <div className="fixed bottom-0 w-full">
                      <PrimaryButtonFullWidth
                        data-testid="button-save-booknow-id"
                        label={saveLabel}
                        onClick={onClick}
                      />
                    </div>
                  )}
                />
              ) : (
                <InputDateRangeDropdown
                  note={isJakartaLocation ? undefined : t("price_note")}
                  setDateValues={setDateValues}
                  endDate={endDate}
                  startDate={startDate}
                  dateRate={dateRate}
                />
              )}
            </div>
            <div className="col-span-2 mt-16 border lg:border-0 border-gray-light lg:col-span-1 lg:mt-0 shadow-input-booking lg:shadow-none">
              {renderForMobile(breakpoints) ? (
                <RoomGuestSelectDrawer
                  rooms={guest.rooms}
                  adults={guest.adults}
                  children={guest.children}
                  infants={guest.infants}
                  onSave={setGuestCount}
                  location={location}
                />
              ) : (
                <RoomGuestSelect
                  rooms={guest.rooms}
                  adults={guest.adults}
                  children={guest.children}
                  infants={guest.infants}
                  setGuestCount={setGuestCount}
                  location={location}
                />
              )}
            </div>
            <div className="col-span-2 my-16 border border-l-0 lg:border-0 lg:border-l border-gray-light shadow-input-booking lg:shadow-none lg:col-span-1 lg:my-0">
              <InputFloatLabel
                name="bookingCode"
                value={bookingCode}
                onChange={(e) => setBookingCode(e.target.value)}
                onKeyPress={validateBookingCodeOnKeyPress}
                label={t("bookingCode")}
                placeholder={t("bookingCodePlaceholder")}
                className="h-full cursor-pointer"
                noBorder
                floatLabelByDefault
                suffixIcon={
                  bookingCode && (
                    <ClearTextInput
                      className="w-20 h-20 cursor-pointer text-gray-dark z-2"
                      onClick={() => setBookingCode("")}
                    />
                  )
                }
                uppercase
              />
            </div>
            <div className="col-span-2 lg:col-span-1 lg:shadow-input-booking">
              <ConditionalLink link={dayjs(endDate).isValid() && link} sameTab>
                {viewRatesButton}
              </ConditionalLink>
            </div>
          </div>
        </div>
      </div>

      {isJakartaLocation && (isGeolocationSG || isGeolocationUS) && (
        <>
          <div className="w-full h-1 bg-gray-med lg:w-1 lg:h-45" />
          <a
            href={jakartaLink}
            rel="noopener noreferrer"
            className="p-14 px-20 text-center border uppercase border-gold rounded-sm text-gold text-14-22 font-semibold self-stretch flex items-center justify-center font-mulish">
            {t("View Rates")}
          </a>
        </>
      )}
    </div>
  );
};
BookingFormHorizontal.defaultProps = {
  forceMobile: false,
};
export default BookingFormHorizontal;
